import { FC, useContext, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Stack, Text, Toggle } from "@fluentui/react";
import { useUserSettings } from "@cayo/core/components/GlobalHooks/userSettingsHook";
import { AppContext } from "@cayo/shared/components/App/appContext";
import { generalSettingsMessages } from "./messages";
import { getCachedServerTheme } from "../../themes";
import { useToggleNavCollapsed } from "../Nav/nav-hooks";
import { ThemeChoice } from "./components/ThemeChoice/ThemeChoice";
import { getThemePreviewColors } from "./utils/getThemePreviewColors";

interface GeneralSettingsPanelProps {
  title?: string;
}

export const GeneralSettingsPanel: FC<GeneralSettingsPanelProps> = () => {
  const { manifest } = useContext(AppContext);
  const intl = useIntl();
  const knownThemes = manifest?.client_config?.themes ?? [];

  const { userSettings, setUserSettings } = useUserSettings();
  const { collapsed, toggleCollapsed } = useToggleNavCollapsed();
  const initialTheme =
    knownThemes.find((t) => t.toLocaleLowerCase() === userSettings.theme?.toLocaleLowerCase()) ||
    manifest?.client_config?.default_theme ||
    "Default";

  const [selected, setSelected] = useState(initialTheme);

  const handleChange = (theme: string) => {
    setSelected(theme);
    setUserSettings({ ...userSettings, theme: theme });
  };

  const themeOptions = useMemo(
    () =>
      knownThemes.map((theme) => ({
        key: theme,
        text: theme,
        previewColors: getThemePreviewColors(getCachedServerTheme(theme)),
      })),
    [knownThemes]
  );

  return (
    <Stack styles={{ root: { paddingTop: "10px" } }} tokens={{ childrenGap: 8 }}>
      <Stack horizontal horizontalAlign="space-between">
        <Text block variant="mediumPlus">
          {intl.formatMessage(generalSettingsMessages.closedSidebar)}
        </Text>
        <Toggle checked={collapsed} onChange={toggleCollapsed} role="menuitemcheckbox" />
      </Stack>
      <Stack>
        <Text variant="mediumPlus">
          {intl.formatMessage(generalSettingsMessages.chooseYourTheme)}
        </Text>
        <ThemeChoice selectedKey={selected} options={themeOptions} onChange={handleChange} />
      </Stack>
    </Stack>
  );
};
